import { ListFilter } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'

import { Button } from '@/modules/shared/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/modules/shared/components/ui/dialog'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
} from '@/modules/shared/components/ui/form'
import { Label } from '@/modules/shared/components/ui/label'
import {
	RadioGroup,
	RadioGroupItem,
} from '@/modules/shared/components/ui/radio-group'
import { Search, SearchProps } from '@/modules/shared/components/ui/search'
import type { PayloadSearch } from '@/modules/shared/types/payload-search.type'
import useOrderFormParams from '../../../hooks/use-order-form-params.hook'
import { useProducts } from '../../../hooks/use-products.hook'
import { useProductSearchInputStore } from '../../../stores/product-search-input.store'
import { PRODUCT_SEARCH_PARAMS } from './product-search.constants'


type FormSearchInputComponentProps = {
	inputRef: React.RefObject<HTMLInputElement>
	quantityRef: React.RefObject<HTMLInputElement>
	isLoadingProducts: boolean
	formSearchInputController: {
		iconOnly?: boolean
		onSubmitRequest: ({ payload }: PayloadSearch) => void
	}
}

export function ProductSearchInputComponent({
	formSearchInputController,
	inputRef,
	quantityRef,
	isLoadingProducts
}: FormSearchInputComponentProps) {
	const [isOpen, setIsOpen] = useState(false)
	const [hasOneProduct, setHasOneProduct] = useState(false)
	const enterStartedInSearchRef = useRef(false)
	const { autoCalculateEnabled } = useOrderFormParams()

	const onClose = () => setIsOpen(false)

	// Evita busca inicial aqui; catálogo controla o fetch inicial
	const { products } = useProducts({ skipInitialFetch: true });

	const form = useForm({
		defaultValues: {
			inputValue: '',
		},
	})

	const {
		defaultFilter,
		defaultTypeFilter,
		actions: { setDefaultFilter, setDefaultTypeFilter },
	} = useProductSearchInputStore()

	const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault()
			// Ignora o Enter que apenas trouxe o foco de outro campo.
			if (!enterStartedInSearchRef.current) return
			enterStartedInSearchRef.current = false
			setHasOneProduct(true)
			form.handleSubmit(handleSubmitRequest)()
		}
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault()
			if (!e.repeat) enterStartedInSearchRef.current = true
		}
	}

	const handleSubmitRequest = (data: { inputValue: string }) => {
		const payload = {
			inputValue: data.inputValue,
			filter: defaultFilter,
			filterType: defaultTypeFilter.toString(),
		}
		formSearchInputController.onSubmitRequest({ payload })
	}

	const handleChangeFilter = (filter: string) => {
		form.setValue('inputValue', '')
		setDefaultFilter(filter)
	}

	// Nova função para aplicar filtros
	const handleApplyFilters = () => {
		// Executa a busca com os filtros atuais
		const currentInputValue = form.getValues('inputValue')
		const payload = {
			inputValue: currentInputValue,
			filter: defaultFilter,
			filterType: defaultTypeFilter.toString(),
		}
		formSearchInputController.onSubmitRequest({ payload })

		// Fecha o modal
		onClose()
	}

	useEffect(() => {
		if (!isLoadingProducts && products?.length === 1 && quantityRef?.current && hasOneProduct) {
			const timeout = setTimeout(() => {
				quantityRef.current.focus()
				quantityRef.current.select()
				setHasOneProduct(false)
			}, 400)

			return () => clearTimeout(timeout)
		}
	}, [isLoadingProducts, products])

	return (
		<div className="flex gap-1 flex-1">
			<Button
				onClick={() => setIsOpen(true)}
				variant="secondary"
				className="p-2  w-12 h-12 text-gray-600"
			>
				<ListFilter size={20} />
			</Button>
			<Form {...form}>
				<form className="flex-1 md:flex-none md:w-96">
					<FormField
						control={form.control}
						name="inputValue"
						render={({ field }) => (
							<FormItem className="flex flex-1 w-full">
								<FormControl>
									<Search
										{...field}
										ref={inputRef}
										onKeyDown={handleKeyDown}

										onFocus={(ev) => {
											ev.currentTarget.select()
										}}
										onKeyUp={handleKeyUp}
										onBlur={() => {
											enterStartedInSearchRef.current = false
											field.onBlur()
										}}
										onChange={(e) => {
											const value = e.currentTarget.value
											field.onChange(value)
										}}
										onClickSearchIcon={form.handleSubmit(handleSubmitRequest)}
										placeholder={`Buscar por ${PRODUCT_SEARCH_PARAMS.find(
											(value) => value.parameter === defaultFilter
										)?.text}`}
									/>
								</FormControl>
							</FormItem>
						)}
					/>
				</form>
			</Form>

			<Dialog open={isOpen} onOpenChange={onClose}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Filtros</DialogTitle>
					</DialogHeader>

					<div className="space-y-4">
						<div>
							<p className="mb-2 text-sm font-medium">Campos</p>
							<RadioGroup value={defaultFilter} className="grid grid-cols-2 gap-2">
								{PRODUCT_SEARCH_PARAMS.map((item) => (
									<div
										onClick={() => handleChangeFilter(item.parameter)}
										onKeyDown={(e) => {
											if (e.key === 'Enter' || e.key === ' ') {
												handleChangeFilter(item.parameter)
											}
										}}
										tabIndex={0}
										role="button"
										key={item.id}
										className="flex items-center space-x-2 p-3 rounded-md border border-gray-200"
									>
										<RadioGroupItem
											key={item.id}
											id={item.id.toString()}
											value={item.parameter.toString()}
										/>
										<Label htmlFor={item.id.toString()} className="text-sm">
											{item?.text}
										</Label>
									</div>
								))}
							</RadioGroup>
						</div>
						<div className="pt-6">
							<p className="mb-2 text-sm font-medium">Tipo de Filtro</p>
							<RadioGroup defaultValue={defaultTypeFilter.toString()} className="flex gap-2">
								{['Igual', 'Iniciando', 'Contendo'].map((type, index) => (
									<div
										key={type}
										onClick={() => setDefaultTypeFilter(Number(index))}
										onKeyDown={(e) => {
											if (e.key === 'Enter' || e.key === ' ') {
												setDefaultTypeFilter(Number(index))
											}
										}}
										tabIndex={0}
										role="button"
										className="flex items-center space-x-2 p-2 border-gray-300"
									>
										<RadioGroupItem
											id={type}
											value={index.toString()}
										/>
										<Label htmlFor={type} className="text-sm">
											{type}
										</Label>
									</div>
								))}
							</RadioGroup>

							<div className="flex mt-6 justify-end">
								<Button size="default" onClick={handleApplyFilters}>
									Aplicar Filtros
								</Button>
							</div>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	)
}